import { isLive } from '@/constants/matchStatus';
import type { LeagueMatch, MatchesByDateLeagueGroup } from '@/types/league';
import { includesNormalized, normalizeSearchText } from '@/utils/searchText';

export interface MatchesByDateFilter {
  liveOnly: boolean;
  /** Raw user input; trimmed and normalised internally. */
  query: string;
}

/**
 * Applies the live-only and text filters to the league groups. A query that
 * matches the league name or country keeps every (live-filtered) match of the
 * league; otherwise only matches whose team names contain the query survive.
 * Groups left without matches are dropped. Returns the input array untouched
 * when no filter is active so memoised consumers keep referential equality.
 */
export function filterMatchesByDateGroups(
  groups: MatchesByDateLeagueGroup[],
  { liveOnly, query }: MatchesByDateFilter,
): MatchesByDateLeagueGroup[] {
  const needle = normalizeSearchText(query.trim());
  if (!liveOnly && !needle) return groups;

  const result: MatchesByDateLeagueGroup[] = [];
  for (const group of groups) {
    const leagueMatches = !needle
      || includesNormalized(group.league.name, needle)
      || includesNormalized(group.league.country, needle);

    const matches = group.matches.filter(match =>
      (!liveOnly || isLive(match.status))
      && (leagueMatches
        || includesNormalized(match.team1, needle)
        || includesNormalized(match.team2, needle)),
    );

    if (matches.length > 0) {
      result.push(matches.length === group.matches.length ? group : { ...group, matches });
    }
  }
  return result;
}

/**
 * Flattens the groups into one list sorted by kickoff time. The sort is
 * stable, so simultaneous kickoffs keep the server's league ordering.
 */
export function flattenMatchesByTime(groups: MatchesByDateLeagueGroup[]): LeagueMatch[] {
  return groups
    .flatMap(group => group.matches)
    .sort((a, b) => Date.parse(a.match_date) - Date.parse(b.match_date));
}

/** True when at least one match across the groups is in play. */
export function hasLiveMatch(groups: readonly MatchesByDateLeagueGroup[]): boolean {
  return groups.some(group => group.matches.some(match => isLive(match.status)));
}

/** Total number of matches across the groups. */
export function countGroupMatches(groups: MatchesByDateLeagueGroup[]): number {
  return groups.reduce((sum, group) => sum + group.matches.length, 0);
}
